import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faRotate,
  faBug,
  faFeather,
  faLightbulb,
  faHandsHelping,
  faHeart,
} from "@fortawesome/free-solid-svg-icons";
import {
  fetchChats,
  fetchFolders,
  createFolder,
  renameFolder,
  deleteFolder,
  updateChatFolder,
} from "./api";

const ChatIndex = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get("page"), 10) || 1;
  const initialFolder = searchParams.get("folder") || "General Inbox";
  const initialPending = searchParams.get("pending") === "true";
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(initialPage);
  const [filterPending, setFilterPending] = useState(initialPending);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(initialFolder);
  const [newFolderName, setNewFolderName] = useState("");
  const [editingFolders, setEditingFolders] = useState(false);
  const limit = 10;

  // sync page and folder to URL
  useEffect(() => {
    setSearchParams({
      page: page.toString(),
      folder: selectedFolder,
      pending: filterPending.toString(),
    });
  }, [page, selectedFolder, filterPending]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await fetchChats(page, limit, false, selectedFolder);
      setChats(data?.chats || []);
      setLoading(false);
    };
    fetchData();
  }, [page, selectedFolder]);

  useEffect(() => {
    const load = async () => {
      const res = await fetchFolders();
      setFolders(res.folders);
    };
    load();
  }, []);

  const handleReload = async () => {
    setLoading(true);
    const data = await fetchChats(page, limit, false, selectedFolder);
    setChats(data?.chats || []);
    setLoading(false);
  };

  const handleSelectFolder = (name) => {
    setSelectedFolder(name);
    setPage(1);
  };

  const handleCreateFolder = async () => {
    if (newFolderName.trim()) {
      await createFolder(newFolderName);
      setNewFolderName("");
      const res = await fetchFolders();
      setFolders(res.folders);
    }
  };

  const handleRename = async (oldName) => {
    const newName = prompt("New folder name:", oldName);
    if (newName) {
      await renameFolder(oldName, newName);
      const res = await fetchFolders();
      setFolders(res.folders);
      if (selectedFolder === oldName) setSelectedFolder(newName);
    }
  };

  const handleDelete = async (name) => {
    if (window.confirm(`Delete folder '${name}'?`)) {
      await deleteFolder(name);
      const res = await fetchFolders();
      setFolders(res.folders);
      if (selectedFolder === name) setSelectedFolder(null);
    }
  };

  const handleChatFolderChangeLocal = async (collection, id, folder) => {
    await updateChatFolder(collection, id, folder);
    handleReload();
  };

  const formatDate = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    // Add 1 hour to messageDate
    messageDate.setHours(messageDate.getHours() + 1);
    const diffInHours = (now - messageDate) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return messageDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffInHours < 48) {
      return "Yesterday";
    } else {
      return messageDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "bug":
        return faBug;
      case "feature":
        return faFeather;
      case "improvement":
        return faLightbulb;
      case "assistance":
        return faHandsHelping;
      case "appreciation":
        return faHeart;
      default:
        return faBug;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "bug":
      case "feature":
      case "improvement":
        return "#3273dc";
      case "assistance":
        return "#ffb700";

      case "appreciation":
        return "#00d1b2";
      default:
        return "#3273dc";
    }
  };

  const getTimeGroup = (date) => {
    const now = new Date();
    const messageDate = new Date(date);
    messageDate.setHours(messageDate.getHours() + 1);
    const diffInHours = (now - messageDate) / (1000 * 60 * 60);

    if (diffInHours < 72) {
      return "recent";
    } else if (diffInHours < 168) {
      return "week";
    } else {
      return "older";
    }
  };

  const renderChats = (chatList) => {
    // accept list of chats
    let currentGroup = null;
    return chatList.map((chat, index) => {
      const timeGroup = getTimeGroup(chat.updated_at);
      let separator = null;

      if (currentGroup !== timeGroup) {
        currentGroup = timeGroup;
        separator = (
          <div key={`separator-${timeGroup}`} className="column is-12">
            <div className="is-flex is-align-items-center my-4">
              <hr
                className="neumorphic-inset"
                style={{
                  flex: 1,
                  height: "2px",
                  background: "var(--border-color)",
                }}
              />
              <span
                className="px-4 subtitle is-6"
                style={{ color: "var(--text-secondary)" }}
              >
                {timeGroup === "week"
                  ? "More than 3 days"
                  : timeGroup === "older"
                  ? "More than 7 days"
                  : "Recent"}
              </span>
              <hr
                className="neumorphic-inset"
                style={{
                  flex: 1,
                  height: "2px",
                  background: "var(--border-color)",
                }}
              />
            </div>
          </div>
        );
      }

      return (
        <React.Fragment key={chat._id}>
          {separator}
          <div className="column is-12">
            <div>
              <Link
                to={{
                  pathname: `/chats/${chat._id}`,
                  search: `?${searchParams.toString()}`,
                }}
                className="chat-card-link"
                style={
                  chat.status === "Closed" || chat.status === "Handled"
                    ? {
                        opacity: 0.45,
                      }
                    : {}
                }
              >
                <div
                  className="neumorphic p-4"
                  style={{
                    background: "var(--bg-secondary)",
                    opacity: chat.read ? 0.8 : 1,
                  }}
                >
                  <div className="columns is-mobile is-vcentered">
                    <div className="column is-2-mobile is-1-tablet">
                      {chat.status !== "Closed" && chat.status !== "Handled" ? (
                        <span
                          className="tag is-danger is-rounded has-text-white has-text-bold"
                          style={{
                            fontWeight: "bold",
                            position: "absolute",
                            marginLeft: "27px",
                            marginTop: "-10px",
                          }}
                        >
                          1
                        </span>
                      ) : null}

                      <div
                        className="neumorphic-inset is-flex is-justify-content-center is-align-items-center"
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: "50%",
                          color: "var(--accent-primary)",
                          fontSize: "1.2em",
                        }}
                      >
                        {chat.user.name[0].toUpperCase()}
                      </div>
                    </div>

                    <div className="column">
                      <div className="is-flex is-justify-content-space-between is-flex-wrap-wrap">
                        <div>
                          <p
                            className="title is-5 mb-1"
                            style={{
                              color: chat.is_premium
                                ? "var(--primary)"
                                : "var(--text-primary)",
                            }}
                          >
                            {chat.user.name}
                          </p>
                          <p
                            className="subtitle is-6 mb-2"
                            style={{ color: "var(--text-secondary)" }}
                          >
                            {(chat.lastMessage?.content || chat.content)?.slice(
                              0,
                              70
                            )}
                            {(chat.lastMessage?.content || chat.content)
                              ?.length > 70
                              ? "..."
                              : ""}
                          </p>
                        </div>
                        <div className="has-text-right">
                          <div className="tags mb-2">
                            {chat.category && chat.category !== "unknown" ? (
                              <span
                                className="tag is-medium neumorphic"
                                style={{
                                  color: getCategoryColor(chat.category),
                                }}
                              >
                                <span className="icon">
                                  <FontAwesomeIcon
                                    icon={getCategoryIcon(chat.category)}
                                  />
                                </span>
                                <span>{chat.category}</span>
                              </span>
                            ) : null}
                            {chat.status && (
                              <span
                                className={`tag is-medium neumorphic`}
                                style={{ color: "black" }}
                              >
                                {chat.status || "Open"}
                              </span>
                            )}
                            {/* folder select */}
                            <div
                              className="select is-medium ml-2"
                              style={{ minWidth: "120px" }}
                            >
                              <select
                                style={{ fontSize: "0.95rem" }}
                                value={chat.folder}
                                onChange={(e) =>
                                  handleChatFolderChangeLocal(
                                    chat.type +
                                      (chat.type === "feedback" ? "s" : "s"),
                                    chat._id,
                                    e.target.value
                                  )
                                }
                              >
                                {folders.map((f) => (
                                  <option key={f} value={f}>
                                    {f}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <p
                            style={{ color: "var(--text-secondary)" }}
                            className="is-size-7"
                          >
                            {formatDate(chat.updated_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </React.Fragment>
      );
    });
  };

  const displayedChats = filterPending
    ? chats.filter(
        (chat) =>
          chat.type === "feedback" && ["Open", "Waiting"].includes(chat.status)
      )
    : chats; // compute filtered list

  if (loading) {
    return (
      <div className="container mt-6 has-text-centered">
        <div className="neumorphic-inset p-6 is-inline-block">
          <div
            className="loader is-loading"
            style={{ width: 50, height: 50 }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="neumorphic p-4 mb-4">
        <div className="is-flex is-justify-content-space-between is-align-items-center">
          <h1
            className="title is-4 mb-0"
            style={{ color: "var(--text-primary)" }}
          >
            Support Chats
          </h1>
          <div className="field has-addons">
            <p className="control">
              <button
                onClick={handleReload}
                className="button is-neumorphic mr-2"
                aria-label="Reload chats"
              >
                <span className="icon">
                  <FontAwesomeIcon icon={faRotate} />
                </span>
              </button>
            </p>
            <p className="control">
              {/* <button
                onClick={() => setFilterPending((p) => !p)}
                className="button is-neumorphic"
              >
                {filterPending ? "All Chats" : "Unread"}
              </button> */}
            </p>
            {page > 1 && (
              <p className="control">
                <button
                  onClick={() => setPage((prev) => prev - 1)}
                  className="button is-neumorphic"
                  aria-label="Previous page"
                >
                  <span className="icon">
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </span>
                </button>
              </p>
            )}
            <p className="control">
              <button
                onClick={() => setPage((prev) => prev + 1)}
                className="button is-neumorphic"
                aria-label="Next page"
              >
                <span className="icon">
                  <FontAwesomeIcon icon={faChevronRight} />
                </span>
              </button>
            </p>
          </div>
        </div>
        {/* folder toolbar */}
        <div className="field is-grouped is-align-items-center mt-3">
          <div
            className="control"
            style={{
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              whiteSpace: "nowrap",
              width: "100%",
            }}
          >
            <div className="tabs is-medium" style={{ display: "inline-flex" }}>
              <ul>
                {folders.map((f) => (
                  <li
                    key={f}
                    className={selectedFolder === f ? "is-active" : ""}
                  >
                    <a onClick={() => handleSelectFolder(f)}>{f}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {editingFolders && (
            <>
              <div className="control">
                <input
                  className="input is-small"
                  placeholder="New folder"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                />
              </div>
              <div className="control">
                <button
                  className="button is-primary is-small"
                  onClick={handleCreateFolder}
                >
                  Add
                </button>
              </div>
            </>
          )}
        </div>
        {/* existing rest of markup... */}
      </div>
      <div className="columns is-multiline">
        {displayedChats.length > 0 ? (
          renderChats(displayedChats)
        ) : (
          <div className="column is-12">
            <div className="neumorphic p-6 has-text-centered">
              <p className="is-size-4" style={{ color: "var(--text-primary)" }}>
                No chats found
              </p>
              <p
                className="is-size-6 mt-2"
                style={{ color: "var(--text-secondary)" }}
              >
                New messages will appear here
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatIndex;
